<template>
  <transition name="fade" mode="out-in">
    <div v-if="score" class="score">
      <div class="totalScore">
        <div>
          <span>{{ score['total'] }}</span><span>/ 10</span>
        </div>
        <div class="details">{{ gettext('Locaties 5km rondom scoren gemiddeld') }} <span>{{ betterThanNearby }}</span></div>
      </div>
      <div class="scoreItem">
        <div class="header">
          <div class="icon-transport" :style="{ backgroundImage: 'url(' + mobiscoreSvgStaticUrl + ')' }"></div>
          <div class="scoreItemTitle">{{ gettext('Openbaar vervoer') }}</div>
        </div>
        <div class="scoreItemDetails">{{ score['public_transit'][1] }}</div>
        <div :class="publicTransitRatingClass" :title="score['public_transit'][0]" :style="{ backgroundImage: 'url(' + mobiscoreSvgStaticUrl + ')' }"></div>
      </div>
      <div class="scoreItem">
        <div class="header">
          <div class="icon-education" :style="{ backgroundImage: 'url(' + mobiscoreSvgStaticUrl + ')' }"></div>
          <div class="scoreItemTitle">{{ gettext('Onderwijs') }}</div>
        </div>
        <div class="scoreItemDetails">{{ score['education'][1] }}</div>
        <div :class="educationRatingClass" :title="score['education'][0]" :style="{ backgroundImage: 'url(' + mobiscoreSvgStaticUrl + ')' }"></div>
      </div>
      <div class="scoreItem">
        <div class="header">
          <div class="icon-shops" :style="{ backgroundImage: 'url(' + mobiscoreSvgStaticUrl + ')' }"></div>
          <div class="scoreItemTitle">{{ gettext('Winkels & diensten') }}</div>
        </div>
        <div class="scoreItemDetails">{{ score['retail'][1] }}</div>
        <div :class="retailRatingClass" :title="score['retail'][0]" :style="{ backgroundImage: 'url(' + mobiscoreSvgStaticUrl + ')' }"></div>
      </div>
      <div class="scoreItem">
        <div class="header">
          <div class="icon-culture" :style="{ backgroundImage: 'url(' + mobiscoreSvgStaticUrl + ')' }"></div>
          <div class="scoreItemTitle">{{ gettext('Ontspanning, sport & cultuur') }}</div>
        </div>
        <div class="scoreItemDetails">{{ score['recreation'][1] }}</div>
        <div :class="recreationRatingClass" :title="score['recreation'][0]" :style="{ backgroundImage: 'url(' + mobiscoreSvgStaticUrl + ')' }"></div>
      </div>
      <div class="scoreItem">
        <div class="header">
          <div class="icon-health" :style="{ backgroundImage: 'url(' + mobiscoreSvgStaticUrl + ')' }"></div>
          <div class="scoreItemTitle">{{ gettext('Gezondheid & zorg') }}</div>
        </div>
        <div class="scoreItemDetails">{{ score['health'][1] }}</div>
        <div :class="healthRatingClass" :title="score['health'][0]" :style="{ backgroundImage: 'url(' + mobiscoreSvgStaticUrl + ')' }"></div>
      </div>
    </div>
    <div v-else-if="errorMsg" class="error">{{ errorMsg }}</div>
    <div v-else class="placeholder">
      <spinner-element/>
      <div>{{ gettext('Mobiscore aan het opvragen') }}</div>
    </div>
  </transition>
</template>

<script>
import {ref, computed, onMounted, watch} from 'vue';
import axios from "axios";
import {useGettext} from 'vue3-gettext';
import SpinnerElement from "@/components/SpinnerElement";

const validateInput = (coords, x, y) => {
  return coords.length === 2 && !isNaN(x) && !isNaN(y);
}

export default {
  name: 'MobiscoreComponent',
  components: {SpinnerElement},
  props: {
    x: {
      type: String,
      required: false,
      default: ''
    },
    y: {
      type: String,
      required: false,
      default: ''
    },
    mobiscorestaticurl: {
      type: String,
      required: false,
      default: '/static/images/mobiscore.svg'
    },
    enableInputListener: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  setup(props) {
    /* translations */
    const { $gettext } = useGettext();
    const gettext = $gettext;

    const x = ref(parseFloat(props.x));
    const y = ref(parseFloat(props.y));
    const score = ref(null);
    const errorMsg = ref('');
    const mobiscoreSvgStaticUrl = ref(props.mobiscorestaticurl);

    // individual ratings
    const publicTransit = ref(0);
    const education = ref(0);
    const retail = ref(0);
    const recreation = ref(0);
    const health = ref(0);
    const betterThanNearby = ref(''); // score of this property is better than others nearby

    const ERROR_MESSAGES = {
      COORDINATES_UNAVAILABLE: gettext('Mobiscore kon niet berekend worden: coördinaten niet ingevuld.'),
      SERVER_UNAVAILABLE: gettext('Mobiscore server niet bereikbaar.'),
      NO_DATA_AVAILABLE: gettext('Geen mobiscore beschikbaar voor deze coördinaten.'),
      REGION_NOT_SUPPORTED: gettext('Deze regio ondersteunt geen mobiscore.'),
      UNKNOWN_ERROR: gettext('Er is een fout opgetreden bij het ophalen van de mobiscore.')
    };

    watch(
      () => [props.x, props.y],
      ([newX, newY]) => {
        const xVal = parseFloat(newX);
        const yVal = parseFloat(newY);

        if (!isNaN(xVal) && !isNaN(yVal)) {
          x.value = xVal;
          y.value = yVal;

          errorMsg.value = '';
          fetchMobiscore(x, y);
        } else {
          handleErrorMessage(ERROR_MESSAGES.INVALID_COORDINATES);
        }
      }
    );

    const fetchMobiscore = async (x, y) => {
      score.value = null;
      errorMsg.value = '';

      try {
        const response = await axios.get(`/reports/mobiscore?x=${x.value}&y=${y.value}`);
        score.value = response.data;

        publicTransit.value = parseFloat(response.data['public_transit'][0]);
        education.value = parseFloat(response.data['education'][0]);
        retail.value = parseFloat(response.data['retail'][0]);
        recreation.value = parseFloat(response.data['recreation'][0]);
        health.value = parseFloat(response.data['health'][0]);
        betterThanNearby.value = response.data['better_than_nearby'];
      } catch (error) {
        if (error.response?.status === 503) {
          handleErrorMessage(ERROR_MESSAGES.SERVER_UNAVAILABLE);
        } else if (error.response?.status === 404) {
          handleErrorMessage(ERROR_MESSAGES.NO_DATA_AVAILABLE);
          if (error.response.data?.msg === 'Region not supported') {
            handleErrorMessage(ERROR_MESSAGES.REGION_NOT_SUPPORTED)
          }
        } else {
          handleErrorMessage(ERROR_MESSAGES.UNKNOWN_ERROR);
        }
      }
    }

    const publicTransitRatingClass = computed(() => computeScoreClass(publicTransit.value));
    const educationRatingClass = computed(() => computeScoreClass(education.value));
    const retailRatingClass = computed(() => computeScoreClass(retail.value));
    const recreationRatingClass = computed(() => computeScoreClass(recreation.value));
    const healthRatingClass = computed(() => computeScoreClass(health.value));

    const computeScoreClass = (value) => {
      if (value >= 0 && value <= 0.9) {
        return 'score-1';
      } else if (value >= 1 && value <= 1.99) {
        return 'score-2';
      } else if (value >= 2 && value <= 2.99) {
        return 'score-3';
      } else if (value >= 3 && value <= 3.99) {
        return 'score-4';
      } else {
        return 'score-5';
      }
    };

    const handleErrorMessage = (msg) => {
      errorMsg.value = msg;
      score.value = null;
    };

    onMounted(() => {
      setTimeout(() => {
        if (!isNaN(x.value) && !isNaN(y.value)) {
          fetchMobiscore(x, y);
        } else if (props.x && props.y) {
          handleErrorMessage(ERROR_MESSAGES.COORDINATES_UNAVAILABLE);
        }

        if (props.enableInputListener) {
          const coordinatesInput = document.getElementById('coordinates');
          if (coordinatesInput) {
            coordinatesInput.addEventListener('change', () => {
              const newCoords = coordinatesInput.value.trim().split(" ");
              const [yCoord, xCoord] = newCoords.map(parseFloat);

              if (validateInput(newCoords, xCoord, yCoord)) {
                x.value = xCoord;
                y.value = yCoord;
                fetchMobiscore(x, y);
              } else {
                handleErrorMessage(ERROR_MESSAGES.INVALID_COORDINATES);
              }
            });
          }
        }
      }, 300);
    })

    return {
      gettext,
      score,
      errorMsg,
      publicTransitRatingClass,
      educationRatingClass,
      retailRatingClass,
      recreationRatingClass,
      healthRatingClass,
      betterThanNearby,
      mobiscoreSvgStaticUrl
    };
  },

};
</script>


<style scoped lang="scss">
$mobiscore-color: #5AB9A0;
$secondary-color: #417868;

button {
  margin-bottom: 20px;
}

div.score {
  display: flex;
  border: 2px solid $mobiscore-color;
  text-align: center;
}

div.score > div {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  gap: 15px;
}

div.scoreItem {
  padding-top: 10px;
  padding-bottom: 10px;
}

div.scoreItem > div:first-child > div:first-child {
  height: 40px;
  width: 40px;
  background-repeat: no-repeat;
  background-size: 2000px 600px;
  margin: 0 auto;
}

div.scoreItem > div:first-child > div:first-child > img {
  height: 40px;
  width: auto;
}

div.scoreItem > div:last-child {
  align-self: center;
  margin-top: auto;
}

div.score > div:nth-child(n+3) {
  border-left: 1px solid #D5D5D5;
}

div.totalScore {
  background: $mobiscore-color;
  gap: 10px;
  padding: 4.5rem 0;
}

div.totalScore > div.details {
  margin: 0 5px;
}

div.totalScore > div:first-child > span:first-child {
  color: #ffffff;
  font-size: 3.5rem;
  font-weight: 700;
}

div.totalScore > div:first-child > span:last-child {
  font-size: 1.5rem;
  color: $secondary-color;
  font-weight: 600;
  margin-left: 5px;
}

div.totalScore > div:last-child {
  font-size: 1rem;
  color: #ffffff;
}

div.totalScore > div:last-child > span {
  font-weight: 600;
}

div.scoreItem > div > div.scoreItemTitle {
  font-size: 1.2rem;
  color: $mobiscore-color;
  font-weight: 600;
}

div.scoreItem > div.header {
  min-height: 80px;
}

div.placeholder {
  background: #f9f9f9;
  border-radius: 5px;
  padding: 20px 30px;
  font-weight: bold;
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: center;
}

.score-1, .score-2, .score-3, .score-4, .score-5 {
  border-radius: 100%;
  font-weight: bold;
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  cursor: help;
  user-select: none;
  background-repeat: no-repeat;
  background-size: 2000px 600px;
  background-position: -940px -240px;
}

.score-1 {
  background-color: #d53e5e;
}

.score-2 {
  background-color: #ef7d2a;
}

.score-3 {
  background-color: #f5c217;
}

.score-4 {
  background-color: #a3cc00;
  background-position: -840px -240px;
}

.score-5 {
  background-color: #4bb342;
  background-position: -840px -240px;
}

.icon-transport {
  background-position: -1330px -130px;
}

.icon-education {
  background-position: -1030px -130px;
}

.icon-shops {
  background-position: -1430px -130px;
}

.icon-culture {
  background-position: -1230px -130px;
}

.icon-health {
  background-position: -1130px -130px;
}

div.error {
  color: #8a6d3b;
  background-color: #fcf8e3;
  border-color: #faebcc;
  padding: 20px 30px;
  border-radius: 5px;
  font-weight: bold;
}

@media (max-width: 550px) {
   div.score {
     flex-direction: column;

     div {
       flex-grow: 1;
     }

     div:nth-child(n+3) {
       border-left: unset;
       border-top: 1px solid #D5D5D5;
     }
   }
}


.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>